import React from 'react';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import Typography from '@material-ui/core/Typography';
import { WithStyles, withStyles } from '@material-ui/core/styles';
import { WithTranslation, withTranslation } from 'react-i18next';

import { ErrorPayload } from '../../redux/reducers/errors';
import styles from './loading-error-style';

const NOT_FOUND = 'not_found';

/**
 * The API reports a missing entity as a 400 naming the offending parameter:
 * `{ error: 'invalid_request', details: { driver_id: { error: 'not_found', … } } }`.
 * Worth telling apart, because retrying it will fail exactly the same way.
 */
const isNotFound = (error?: ErrorPayload): boolean => {
    if (!error) {
        return false;
    }
    if (error.error === NOT_FOUND) {
        return true;
    }
    const { details } = error;

    return (
        !!details &&
        typeof details === 'object' &&
        Object.keys(details).some((key) => details[key]?.error === NOT_FOUND)
    );
};

/** The human-readable half of the payload, wherever the endpoint chose to put it. */
const describe = (error?: ErrorPayload): string => {
    if (!error) {
        return '';
    }
    const { error_description, description, detail, localized_error, details, message } = error;
    const detailed =
        details && typeof details === 'object'
            ? Object.keys(details)
                  .map((key) => details[key]?.description)
                  .filter(Boolean)
                  .join(' ')
            : details;

    return error_description || description || detail || detailed || localized_error || message || '';
};

/**
 * Shown in place of the page loader when the request that feeds the page failed.
 * Without it a failed fetch leaves the spinner running with no way out, which
 * reads as "still loading" for ever.
 */
interface LoadingErrorProps extends WithStyles, WithTranslation {
    error?: ErrorPayload;
    onRetry?: () => void;
}

const LoadingError = (props: LoadingErrorProps): JSX.Element => {
    const { t, classes, error, onRetry } = props;
    const notFound = isNotFound(error);
    const reason = notFound
        ? t('common:errors.not_found_hint')
        : describe(error) || t('common:errors.load_failed_hint');

    return (
        <div className={classes.root}>
            <Icon className={classes.icon} fontSize="large">
                {notFound ? 'search_off' : 'cloud_off'}
            </Icon>
            <Typography variant="h6">
                {t(notFound ? 'common:errors.not_found' : 'common:errors.load_failed')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
                {reason}
            </Typography>
            {onRetry && !notFound && (
                <Button variant="contained" color="primary" onClick={onRetry} startIcon={<Icon>refresh</Icon>}>
                    {t('common:errors.retry')}
                </Button>
            )}
        </div>
    );
};

export default withTranslation(['common'])(withStyles(styles)(LoadingError));
